/**
 * 推理可视化组件，负责展示模型推理过程的赛博朋克风格动效
 */
export class ReasoningVisualizer {
  private static instance: ReasoningVisualizer;
  
  // DOM元素
  private container: HTMLDivElement | null = null;
  private contentElement: HTMLDivElement | null = null;
  private statusBar: HTMLDivElement | null = null;
  private modelInfo: HTMLDivElement | null = null;
  
  // 状态
  private isActive = false;
  private reasoningContent = '';
  private modelId = '';
  private vendor = '';
  private animationFrame: number | null = null;
  private startTime: number = 0;
  
  // 文本处理
  private contentLines: string[] = [];
  private displayedLines: number = 3;
  private scrollIndex: number = 0;
  private lastUpdateTime: number = 0;
  private scrollSpeed: number = 40;
  
  // 线条动画
  private linesCanvas: HTMLCanvasElement | null = null;
  private linesCtx: CanvasRenderingContext2D | null = null;
  private lines: Line[] = [];
  private lineCount: number = 8;
  
  private progress: number = 0;
  
  /**
   * 私有构造函数
   */
  private constructor() {
    // 创建初始DOM结构
    this.createDomStructure();
    
    // 添加键盘事件（ESC键关闭）
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isActive) {
        this.hide();
      }
    });
    
    // 添加窗口大小变化事件
    window.addEventListener('resize', () => {
      if (this.isActive) {
        this.resizeCanvases();
      }
    });
  }
  
  /**
   * 获取单例实例
   */
  static getInstance(): ReasoningVisualizer {
    console.log('获取ReasoningVisualizer实例');
    if (!ReasoningVisualizer.instance) {
      console.log('创建新的ReasoningVisualizer实例');
      ReasoningVisualizer.instance = new ReasoningVisualizer();
    } else {
      console.log('返回已有的ReasoningVisualizer实例');
    }
    return ReasoningVisualizer.instance;
  }
  
  /**
   * 显示推理可视化
   * @param modelId 模型ID
   * @param vendor 供应商
   */
  show(modelId: string, vendor: string = '未知'): void {
    console.log(`ReasoningVisualizer.show被调用 - 模型: ${modelId}, 供应商: ${vendor}`);
    
    // 如果之前的实例没被正确清理，强制重置
    if (this.isActive && !this.container) {
      console.log('检测到异常状态：活动但无容器，强制重置状态');
      this.isActive = false;
    }
    
    if (this.isActive) {
      console.log('ReasoningVisualizer已经是活动状态，忽略show调用');
      return;
    }
    
    // 确保在显示前重置状态
    this.reasoningContent = '';
    this.progress = 0;
    this.isActive = true;
    this.modelId = modelId;
    this.vendor = vendor;
    this.startTime = Date.now();
    
    console.log('ReasoningVisualizer正在构建DOM...');
    
    // 必须创建新的DOM结构
    if (this.container) {
      console.log('清除旧的容器');
      if (this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
    }
    
    // 创建新的DOM结构
    this.createDomStructure();
    this.addStyles();
    
    // 更新模型信息
    if (this.modelInfo) {
      this.modelInfo.textContent = `Model: ${this.modelId} | Source: ${this.vendor}`;
    } else {
      console.error('无法获取modelInfo元素');
    }
    
    // 初始化状态栏
    if (this.statusBar) {
      this.statusBar.innerHTML = this.generateStatusBarContent(this.progress);
    } else {
      console.error('无法获取statusBar元素');
    }
    
    // 初始化内容
    this.contentLines = this.getInitialContent();
    this.updateTextContent();
    
    // 显示容器
    if (this.container) {
      console.log('设置ReasoningVisualizer的可见性...');
      (this.container as HTMLDivElement).style.opacity = '1';
      (this.container as HTMLDivElement).classList.add('active');
    } else {
      console.error('ReasoningVisualizer容器不存在！');
    }
    
    console.log('ReasoningVisualizer启动动画...');
    
    // 开始动画
    this.startAnimationLoop();
    
    console.log('ReasoningVisualizer.show完成');
  }
  
  /**
   * 获取初始内容
   */
  private getInitialContent(): string[] {
    const placeholderLines = [
      '[信息] 正在连接AI思维中枢...',
      '[信息] 初始化神经网络引擎...',
      '[信息] 准备接收AI响应数据...',
      '[信息] 正在校准智能模型参数...',
      '[信息] 启动虚拟对话环境...',
      '[信息] 连接量子计算资源...',
      '[信息] 正在加载语言理解模块...',
      '[信息] 请耐心等待系统响应...',
      '[信息] 调整智能交互引擎...',
      '[信息] 建立安全通信通道...',
      '[信息] 正在优化思维算法...',
      '[信息] 等待AI思维形成中...'
    ];
    
    return [...placeholderLines].sort(() => 0.5 - Math.random());
  }
  
  /**
   * 更新推理内容
   * @param content 推理内容
   */
  update(content: string): void {
    if (!this.isActive) return;
    
    this.reasoningContent = content;
    
    const lines = content.trim().split('\n');
    const filteredLines = lines.filter(line => line.trim().length > 0);
    
    if (filteredLines.length > 0) {
      this.contentLines = filteredLines;
    }
    
    this.updateTextContent();
    
    // 更新状态栏
    if (this.statusBar) {
      this.statusBar.innerHTML = this.generateStatusBarContent(this.progress);
    }
  }
  
  /**
   * 隐藏推理可视化
   */
  hide(): void {
    if (!this.isActive) return;
    
    console.log('ReasoningVisualizer.hide被调用');
    
    this.isActive = false;
    
    // 停止动画循环
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    // 隐藏容器
    if (this.container) {
      (this.container as HTMLDivElement).style.opacity = '0';
      (this.container as HTMLDivElement).classList.remove('active');
      
      // 延迟后移除DOM元素
      setTimeout(() => {
        if (this.container && this.container.parentNode) {
          this.container.parentNode.removeChild(this.container);
          this.container = null;
          this.contentElement = null;
          this.statusBar = null;
          this.modelInfo = null;
          this.linesCanvas = null;
          this.linesCtx = null;
        }
        
        console.log('ReasoningVisualizer容器已完全移除');
        // 确保重置所有状态
        this.reasoningContent = '';
        this.progress = 0;
        
      }, 500);
    } else {
      // 确保重置所有状态
      this.reasoningContent = '';
      this.progress = 0;
      console.log('ReasoningVisualizer容器为空，无法移除');
    }
  }
  
  /**
   * 获取当前是否活跃
   */
  isVisible(): boolean {
    return this.isActive;
  }
  
  /**
   * 重新调整画布大小
   */
  private resizeCanvases(): void {
    // 方法保留但不执行任何操作
  }
  
  /**
   * 创建DOM结构
   */
  private createDomStructure(): void {
    // 创建主容器
    this.container = document.createElement('div');
    this.container.className = 'reasoning-visualizer';
    this.container.innerHTML = `
      <div class="visualizer-inner">
        <div class="holo-frame">
          <div class="corner top-left"></div>
          <div class="corner top-right"></div>
          <div class="corner bottom-left"></div>
          <div class="corner bottom-right"></div>
          <div class="edge top"></div>
          <div class="edge right"></div>
          <div class="edge bottom"></div>
          <div class="edge left"></div>
        </div>
        
        <div class="visualizer-header">
          <div class="header-title">
            <span class="header-title-text">NEURAL LINK</span>
          </div>
          <div class="header-model" id="model-info">加载中...</div>
        </div>
        
        <div class="visualizer-content" id="content-container"></div>
        
        <div class="visualizer-footer">
          <div class="status-bar" id="status-bar">系统启动中...</div>
        </div>
        
        <div class="scan-line"></div>
      </div>
    `;
    
    console.log('ReasoningVisualizer创建DOM结构');
    document.body.appendChild(this.container);
    
    // 获取元素引用
    this.contentElement = document.getElementById('content-container') as HTMLDivElement;
    this.statusBar = document.getElementById('status-bar') as HTMLDivElement;
    this.modelInfo = document.getElementById('model-info') as HTMLDivElement;
    
    if (!this.contentElement) console.error('无法获取content-container元素');
    if (!this.statusBar) console.error('无法获取status-bar元素');
    if (!this.modelInfo) console.error('无法获取model-info元素');
    
    // 初始化状态栏
    if (this.statusBar) {
      this.statusBar.innerHTML = this.generateStatusBarContent(0);
    }
  }
  
  /**
   * 更新文本内容
   */
  private updateTextContent(): void {
    if (!this.contentElement || this.contentLines.length === 0) return;
    
    const now = Date.now();
    if (now - this.lastUpdateTime >= this.scrollSpeed) {
      this.lastUpdateTime = now;
      this.scrollIndex = (this.scrollIndex + 1) % this.contentLines.length;
    }
    
    let html = '';
    for (let i = 0; i < this.displayedLines; i++) {
      const lineIndex = (this.scrollIndex + i) % this.contentLines.length;
      const line = this.contentLines[lineIndex];
      const lineNumber = (lineIndex + 1).toString().padStart(3, '0');
      
      // 计算透明度：第一行和第三行半透明，中间行完全不透明
      let opacity = 1;
      if (i === 0) {
        opacity = 0.3; // 第一行较透明
      } else if (i === 2) {
        opacity = 0.3; // 第三行较透明
      }
      
      html += `<div class="text-line" style="opacity: ${opacity}">
        <span class="line-number">${lineNumber}</span>
        <span class="line-content">${this.enhanceLine(line)}</span>
      </div>`;
    }
    
    this.contentElement.innerHTML = html;
  }
  
  /**
   * 增强文本行
   */
  private enhanceLine(line: string): string {
    if (!line) return '<span class="null-text">[NULL_DATA]</span>';
    
    // 高亮关键词
    const keywords = ['模型', '推理', '神经', '网络', '分析', '计算', '结果', '预测', 'AI', '智能', '学习', '数据', '黑墙', '赛博', '电子', '量子'];
    let enhancedLine = line;
    
    // 转换数字为十六进制
    enhancedLine = enhancedLine.replace(/\d+/g, (match) => {
      const hex = parseInt(match).toString(16).toUpperCase();
      return `<span class="hex-text">0x${hex}</span>`;
    });
    
    // 随机添加故障效果
    if (Math.random() < 0.1) {
      const glitchText = enhancedLine.split('').map(char => {
        if (Math.random() < 0.2) {
          return `<span class="glitch-text">${char}</span>`;
        }
        return char;
      }).join('');
      enhancedLine = glitchText;
    }
    
    // 高亮关键词
    keywords.forEach(keyword => {
      enhancedLine = enhancedLine.replace(
        new RegExp(keyword, 'g'),
        `<span class="highlight">${keyword}</span>`
      );
    });
    
    // 转换方括号内的文本
    enhancedLine = enhancedLine.replace(/\[(.*?)\]/g, (match, content) => {
      return `<span class="bracket-text">[${content}]</span>`;
    });
    
    return enhancedLine;
  }
  
  /**
   * 生成状态栏内容
   */
  private generateStatusBarContent(progress: number): string {
    const progressBar = Math.floor(progress / 4);
    const bar = '█'.repeat(progressBar) + '░'.repeat(25 - progressBar);
    const percentage = Math.floor(progress);
    const timestamp = new Date().toISOString().slice(11, 19);
    
    // 收到内容后状态显示为ACTIVE，否则为CONNECTING
    const status = this.reasoningContent ? 'ACTIVE' : 'CONNECTING';
    
    return `<div class="status-line"><span class="status-item">[${timestamp}]</span> NEURAL_LINK <${bar}> ${percentage}%</div>
            <div class="status-line">STATUS: ${status} | MEM: ${Math.floor(Math.random() * 30 + 70)}% | CORE: 1/2</div>`;
  }
  
  /**
   * 初始化线条
   */
  private initLines(): void {
    // 保留方法但不初始化线条
    this.lines = [];
  }
  
  /**
   * 开始动画循环
   */
  private startAnimationLoop(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    let lastTime = Date.now();
    
    const animate = () => {
      if (!this.isActive) return;
      
      const now = Date.now();
      const deltaTime = now - lastTime;
      lastTime = now;
      
      // 每秒增加5%的进度，最大到90%
      this.progress += (deltaTime / 1000) * 5;
      this.progress = Math.min(90, this.progress);
      
      // 更新状态栏
      if (this.statusBar) {
        this.statusBar.innerHTML = this.generateStatusBarContent(this.progress);
      }
      
      // 更新文本内容
      this.updateTextContent();
      
      // 随机触发故障效果
      if (Math.random() < 0.01) {
        this.triggerGlitchEffect();
      }
      
      this.animationFrame = requestAnimationFrame(animate);
    };
    
    this.animationFrame = requestAnimationFrame(animate);
  }
  
  /**
   * 触发故障效果
   */
  private triggerGlitchEffect(): void {
    if (!this.container) return;
    
    this.container.classList.add('glitch');
    setTimeout(() => {
      if (this.container) {
        this.container.classList.remove('glitch');
      }
    }, 200);
  }
  
  /**
   * 添加样式
   */
  private addStyles(): void {
    if (document.getElementById('reasoning-visualizer-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'reasoning-visualizer-styles';
    style.innerHTML = `
      .reasoning-visualizer {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(90vw, 500px);
        height: min(50vh, 200px);
        z-index: 9999;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease;
      }
      
      .reasoning-visualizer.active {
        opacity: 1;
        pointer-events: auto;
      }
      
      @media (max-width: 768px) {
        .reasoning-visualizer {
          width: 90vw;
          height: 180px;
        }
      }
      
      .visualizer-inner {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: rgba(0, 2, 4, 0.95);
        border: 1px solid rgba(255, 0, 0, 0.4);
        box-shadow: 0 0 20px rgba(255, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
      }
      
      .holo-frame {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 10;
      }
      
      .corner {
        position: absolute;
        width: 20px;
        height: 20px;
        border-color: rgba(255, 0, 0, 0.5);
        z-index: 2;
      }
      
      .top-left {
        top: 0;
        left: 0;
        border-top: 2px solid;
        border-left: 2px solid;
      }
      
      .top-right {
        top: 0;
        right: 0;
        border-top: 2px solid;
        border-right: 2px solid;
      }
      
      .bottom-left {
        bottom: 0;
        left: 0;
        border-bottom: 2px solid;
        border-left: 2px solid;
      }
      
      .bottom-right {
        bottom: 0;
        right: 0;
        border-bottom: 2px solid;
        border-right: 2px solid;
      }
      
      .edge {
        position: absolute;
        background-color: rgba(255, 0, 0, 0.2);
        z-index: 1;
      }
      
      .top, .bottom {
        height: 1px;
        width: calc(100% - 40px);
        left: 20px;
      }
      
      .left, .right {
        width: 1px;
        height: calc(100% - 40px);
        top: 20px;
      }
      
      .top { top: 0; }
      .right { right: 0; }
      .bottom { bottom: 0; }
      .left { left: 0; }
      
      .header-title-text {
        font-family: "Rajdhani", "Orbitron", monospace !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        letter-spacing: 1px !important;
        color: rgba(255, 0, 0, 0.8) !important;
        text-shadow: 0 0 8px rgba(255, 0, 0, 0.4) !important;
        text-transform: uppercase !important;
        white-space: nowrap !important;
      }
      
      .visualizer-header {
        height: 36px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 15px;
        border-bottom: 1px solid rgba(255, 0, 0, 0.4);
        background: linear-gradient(90deg, rgba(20, 0, 0, 0.95), rgba(40, 0, 0, 0.95));
        flex-shrink: 0;
      }
      
      .header-decorations {
        display: flex;
        gap: 5px;
      }
      
      .header-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }
      
      .header-dot.red { background-color: rgba(255, 0, 0, 0.7); }
      .header-dot.yellow { background-color: rgba(255, 170, 0, 0.7); }
      .header-dot.green { background-color: rgba(0, 180, 0, 0.7); }
      
      .header-model {
        font-size: 10px;
        color: rgba(255, 0, 0, 0.6);
      }
      
      .visualizer-content {
        flex: 1;
        overflow: hidden;
        padding: 10px 15px;
        position: relative;
        z-index: 2;
        font-family: 'Courier New', monospace;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      
      .visualizer-footer {
        height: 44px;
        padding: 3px 12px;
        background: linear-gradient(90deg, rgba(20, 0, 0, 0.95), rgba(40, 0, 0, 0.95));
        border-top: 1px solid rgba(255, 0, 0, 0.4);
        display: flex;
        align-items: center;
        position: relative;
        z-index: 2;
        flex-shrink: 0;
      }
      
      .status-bar {
        width: 100%;
        font-size: 12px;
        color: rgba(255, 60, 60, 0.9);
        font-family: "Courier New", monospace;
        white-space: nowrap;
        overflow: hidden;
        line-height: 1.5;
        transform: scale(0.95);
        transform-origin: left top;
        letter-spacing: 0.5px;
      }
      
      .status-item {
        color: rgba(255, 80, 80, 1);
        font-weight: bold;
        text-shadow: 0 0 8px rgba(255, 0, 0, 0.4);
      }
      
      .text-line {
        font-size: 14px;
        line-height: 1.8;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: rgba(255, 40, 40, 0.7);
        text-shadow: 0 0 5px rgba(255, 0, 0, 0.3);
        transition: opacity 0.3s ease;
      }
      
      .line-number {
        display: inline-block;
        width: 30px;
        color: rgba(255, 0, 0, 0.6);
        text-shadow: 0 0 8px rgba(255, 0, 0, 0.3);
      }
      
      .line-content {
        color: rgba(255, 40, 40, 0.7);
      }
      
      .highlight {
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 0 0 8px rgba(255, 0, 0, 0.5);
        font-weight: bold;
      }
      
      .null-text {
        color: rgba(255, 0, 0, 0.9);
        font-weight: bold;
        animation: glitch 0.3s infinite;
      }
      
      .hex-text {
        color: rgba(255, 100, 100, 0.8);
        font-family: "Courier New", monospace;
      }
      
      .glitch-text {
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 2px 0 rgba(255, 0, 0, 0.8), -2px 0 rgba(0, 255, 255, 0.8);
        animation: textGlitch 0.1s infinite;
      }
      
      .bracket-text {
        color: rgba(255, 150, 150, 0.9);
        font-weight: bold;
      }
      
      .scan-line {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background: linear-gradient(to bottom, 
                                   rgba(255, 0, 0, 0.4), 
                                   rgba(255, 0, 0, 0.1), 
                                   transparent);
        animation: scanLine 6s linear infinite;
        pointer-events: none;
        z-index: 3;
      }
      
      @keyframes scanLine {
        0% { top: 0; }
        100% { top: 100%; }
      }
      
      @keyframes glitch {
        0% { opacity: 1; }
        50% { opacity: 0.8; }
        51% { opacity: 1; }
        100% { opacity: 1; }
      }
      
      @keyframes textGlitch {
        0% { transform: translate(0); }
        25% { transform: translate(-1px, 1px); }
        50% { transform: translate(1px, -1px); }
        75% { transform: translate(-1px, -1px); }
        100% { transform: translate(0); }
      }
    `;
    
    document.head.appendChild(style);
  }
}

// 线条接口
interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  progress: number;
  speed: number;
  color: string;
  active: boolean;
  delay: number;
} 